import * as React from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase/supabaseClient";
import { toast } from "sonner";
import LoadingSpinner from "@/components/LoadingSpinner";
import { store } from "@/lib/states/store";

export const Route = createFileRoute("/reset-password")({
  component: ResetPage,
  async beforeLoad({ context }) {
    if (!context.session) {
      store.getState().session.reset();
      throw redirect({
        to: "/login",
        replace: true,
      });
    } else if (!store.getState().session.initialised) {
      await store.getState().session.initWithUserAsync(context.session.user);
    }
  },
});

function ResetPage() {
  const navigate = Route.useNavigate();

  const formSchema = z
    .object({
      password: z.string().min(8, "Password must be at least 8 characters"),
      confirmPassword: z.string(),
    })
    .superRefine(({ confirmPassword, password }, ctx) => {
      if (confirmPassword !== password) {
        ctx.addIssue({
          code: "custom",
          message: "Passwords do not match",
          path: ["confirmPassword"],
        });
      }
    });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const [loading, setLoading] = React.useState(false);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setLoading(true);

    const { error } = await supabase.auth.updateUser({
      password: values.password,
    });

    setLoading(false);

    if (error) {
      console.error(error);
      toast.error(error.message);
      form.setError("password", { message: error.message });
      return;
    }

    navigate({ to: "/login", replace: true });
  }

  return (
    <>
      <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
        <div className="flex w-full max-w-sm flex-col gap-6">
          <a
            href="#"
            className="flex items-center gap-2 self-center font-medium"
          >
            <img src="/lockupBlack.svg" alt="Trainrr" className="h-6" />
          </a>

          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-xl">Update your password</CardTitle>
              <CardDescription>
                Enter a new password for your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <div className="grid gap-6">
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>

                          <FormControl>
                            <Input type="password" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm Password</FormLabel>

                          <FormControl>
                            <Input type="password" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button>
                      {loading && <LoadingSpinner />}
                      {!loading && "Reset Password"}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
